import { useI18n } from '@ecom/i18n';
import { Card } from '@ecom/ui-kit';
import { type ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { useMyStoreQuery } from '../api';

import { CardSkeleton } from '@/components/ui/skeletons/CardSkeleton';
import { useInventoryOverviewQuery } from '@/features/inventory/api';
import { useCategoriesQuery, useProductsQuery } from '@/features/products/api';

/**
 * Six criteria, arbitrated explicitly (docs/resumée.txt) — "images
 * présentes" was proposed and REJECTED: no endpoint (merchant or public)
 * can ever list a product's images (docs/resumée.txt entrée 57), and the
 * rule is "no endpoint created solely to feed a score widget". Every
 * criterion below reads data ALREADY fetched by an existing hook — this
 * component performs zero network calls of its own.
 */
interface HealthCriterion {
  readonly key: string;
  readonly label: string;
  readonly met: boolean;
  readonly hint: string;
  /** A real, working next step for this criterion — rendered as a link instead of plain text when present. */
  readonly to?: string;
}

const MAX_SCORE = 100;

export function StoreHealth(): ReactElement {
  const { t } = useI18n();
  const storeQuery = useMyStoreQuery();
  const categoriesQuery = useCategoriesQuery();
  const productsQuery = useProductsQuery();
  const inventory = useInventoryOverviewQuery();

  const isPending =
    storeQuery.isPending ||
    categoriesQuery.isPending ||
    productsQuery.isPending ||
    inventory.isPending;

  if (isPending) return <CardSkeleton />;

  const products = productsQuery.data ?? [];
  const categories = categoriesQuery.data ?? [];

  const criteria: readonly HealthCriterion[] = [
    {
      key: 'store',
      label: t('merchant.store.health.storeConfigured'),
      met: storeQuery.data !== null && storeQuery.data !== undefined,
      hint: t('merchant.store.health.storeConfiguredHint'),
    },
    {
      key: 'categories',
      label: t('merchant.store.health.categoriesPresent'),
      met: categories.length > 0,
      hint: t('merchant.store.health.categoriesPresentHint'),
      to: '/categories',
    },
    {
      key: 'products',
      label: t('merchant.store.health.productsPresent'),
      met: products.length > 0,
      hint: t('merchant.store.health.productsPresentHint'),
    },
    {
      key: 'active-products',
      label: t('merchant.dashboard.kpiActiveProducts'),
      met: products.some((product) => product.status === 'active'),
      hint: t('merchant.store.health.activeProductsHint'),
    },
    {
      key: 'variants',
      label: t('merchant.store.health.variantsPresent'),
      met: inventory.rows.length > 0,
      hint: t('merchant.store.health.variantsPresentHint'),
    },
    {
      key: 'stock',
      label: t('merchant.analytics.stockAvailable'),
      met: inventory.rows.some((row) => row.stock.state === 'in_stock'),
      hint: t('merchant.store.health.stockAvailableHint'),
    },
  ];

  const metCount = criteria.filter((c) => c.met).length;
  const score = Math.round((metCount / criteria.length) * MAX_SCORE);

  return (
    <Card className="flex flex-col gap-4 p-5">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-bold text-ink">
          {t('merchant.store.health.title')}
        </h2>
        <span className="font-display text-2xl font-bold text-accent">
          {score}
          <span className="text-sm text-inkFaint">/100</span>
        </span>
      </div>

      <div className="h-2 w-full rounded-full bg-line">
        <div
          className="h-2 rounded-full bg-accent transition-[width]"
          style={{ width: `${String(score)}%` }}
        />
      </div>

      <ul className="flex flex-col gap-2">
        {criteria.map((criterion) => (
          <li key={criterion.key} className="flex items-start gap-2 text-sm">
            <span
              className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                criterion.met ? 'bg-goodSoft text-good' : 'bg-surface2 text-inkFaint'
              }`}
              aria-hidden="true"
            >
              {criterion.met ? '✓' : '–'}
            </span>
            <span>
              <span className={criterion.met ? 'text-ink' : 'text-inkSoft'}>{criterion.label}</span>
              {!criterion.met ? (
                criterion.to !== undefined ? (
                  <Link
                    to={criterion.to}
                    className="block text-xs font-medium text-accent hover:underline"
                  >
                    {criterion.hint}
                  </Link>
                ) : (
                  <span className="block text-xs text-inkFaint">{criterion.hint}</span>
                )
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
